import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaSearch, FaMoneyBillWave, FaUniversity, FaMoneyCheck, FaArrowLeft } from 'react-icons/fa';
import { ENDPOINT } from '../api/auth';
import './ProductOrder.css';

const banks = [
  { value: 'CBE', label: 'CBE' },
  { value: 'Awash', label: 'Awash' },
  { value: 'Dashen', label: 'Dashen' },
  { value: 'Abyssinia', label: 'Abyssinia' },
  { value: 'Birhan', label: 'Birhan' },
  { value: 'Telebirr', label: 'Telebirr' },
  { value: 'Check', label: 'Check' },
];

const paymentMethods = [
  { value: 'bank', label: 'Bank Transfer', icon: <FaUniversity /> },
  { value: 'cash', label: 'Cash', icon: <FaMoneyBillWave /> },
  { value: 'mixed', label: 'Mixed Payment', icon: <FaMoneyCheck /> },
];

const ProductOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    quantity: '',
    ordered_price: '',
    selling_price: '',
    payment_method: 'bank',
    bank_name: 'CBE',
    paid_amount: ''
  });
  const [errors, setErrors] = useState({});
  const [showProductList, setShowProductList] = useState(true);

  // Toggle between product list and order form
  const toggleView = () => {
    setShowProductList(!showProductList);
  };

  // Handle product selection
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setFormData(prev => ({
      ...prev,
      ordered_price: product.import_price?.toString() || '',
      selling_price: product.selling_price?.toString() || ''
    }));
    setShowProductList(false);
  };

  // Handle search
  const handleSearch = (text) => {
    setSearchQuery(text);
    const filtered = products.filter(
      product => product.name.toLowerCase().includes(text.toLowerCase())
    );
    setFilteredProducts(filtered);
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${ENDPOINT}?action=getProducts`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const productsData = response.data.products || [];
        setProducts(productsData);
        setFilteredProducts(productsData);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Failed to fetch products');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  // Handle back to product list
  const handleBackToList = () => {
    setShowProductList(true);
    setSelectedProduct(null);
    setFormData({
      quantity: '',
      ordered_price: '',
      selling_price: '',
      payment_method: 'bank',
      bank_name: 'CBE',
      paid_amount: ''
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!selectedProduct) newErrors.product = 'Product is required';
    if (!formData.quantity) newErrors.quantity = 'Quantity is required';
    if (!formData.ordered_price) newErrors.ordered_price = 'Order price is required';
    if (!formData.selling_price) newErrors.selling_price = 'Selling price is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        product_id: selectedProduct.id,
        action: 'orderProduct'
      };

      const response = await axios.post(ENDPOINT, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Order created successfully');
        navigate(-1); // Go back to previous page
      } else {
        throw new Error(response.data.message || 'Failed to create order');
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.message || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    const quantity = parseFloat(formData.quantity) || 0;
    const price = parseFloat(formData.ordered_price) || 0;
    return (quantity * price).toFixed(2);
  };

  const calculateUnpaid = () => {
    const total = parseFloat(calculateTotal()) || 0;
    const paid = parseFloat(formData.paid_amount) || 0;
    return (total - paid).toFixed(2);
  };

  // Render product list
  const renderProductList = () => (
    <div className="product-order-container">
      <div className="product-order-card">
        <div className="header-with-back">
          <h2>Select Product</h2>
        </div>
        
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-state">
            <p>No products found</p>
          </div>
        ) : (
          <div className="product-list">
            {filteredProducts.map(product => (
              <div 
                key={product.id} 
                className="product-item"
                onClick={() => handleSelectProduct(product)}
              >
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <div className="product-details">
                    <span className="detail">ቀሪ: {product.quantity || 0}</span>
                    <span className="detail">የገባበት ዋጋ: {product.import_price || 0} ብር</span>
                    <span className="detail">የሽያጭ ዋጋ: {product.selling_price || 0} ብር</span>
                  </div>
                </div>
                <div className="select-button">Select</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render order form
  const renderOrderForm = () => (
    <div className="product-order-container">
      <div className="product-order-card">
        <div className="header-with-back">
          <button className="back-button" onClick={handleBackToList}>
            <FaArrowLeft />
          </button>
          <h2>Place New Order</h2>
        </div>
        
        <div className="selected-product-info">
          <h3 className="selected-product-name">{selectedProduct?.name}</h3>
          <div className="product-meta">
            <span>ቀሪ: {selectedProduct?.quantity || 0}</span>
            <span>የገባበት ዋጋ: {selectedProduct?.import_price || 0} ብር</span>
            <span>የሽያጭ ዋጋ: {selectedProduct?.selling_price || 0} ብር</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="number"
              className={`form-input ${errors.quantity ? 'error' : ''}`}
              placeholder="Quantity"
              value={formData.quantity}
              onChange={(e) => setFormData({...formData, quantity: e.target.value})}
            />
            {errors.quantity && <div className="error-message">{errors.quantity}</div>}
          </div>

          <div className="form-group">
            <input
              type="number"
              className={`form-input ${errors.ordered_price ? 'error' : ''}`}
              placeholder="Ordered Price (per unit)"
              value={formData.ordered_price}
              onChange={(e) => setFormData({...formData, ordered_price: e.target.value})}
              step="0.01"
            />
            {errors.ordered_price && <div className="error-message">{errors.ordered_price}</div>}
          </div>

          <div className="form-group">
            <input
              type="number"
              className={`form-input ${errors.selling_price ? 'error' : ''}`}
              placeholder="Selling Price (per unit)"
              value={formData.selling_price}
              onChange={(e) => setFormData({...formData, selling_price: e.target.value})}
              step="0.01"
            />
            {errors.selling_price && <div className="error-message">{errors.selling_price}</div>}
          </div>

          <div className="form-group">
            <label className="section-title">Payment Method</label>
            <div className="payment-methods">
              {paymentMethods.map(method => (
                <button
                  key={method.value}
                  type="button"
                  className={`payment-method-btn ${formData.payment_method === method.value ? 'active' : ''}`}
                  onClick={() => setFormData({...formData, payment_method: method.value})}
                >
                  <span className="payment-icon">{method.icon}</span>
                  <span>{method.label}</span>
                </button>
              ))}
            </div>
          </div>

          {formData.payment_method === 'bank' && (
            <div className="form-group">
              <select
                className="form-input"
                value={formData.bank_name}
                onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
              >
                {banks.map(bank => (
                  <option key={bank.value} value={bank.value}>
                    {bank.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {formData.payment_method === 'mixed' && (
            <div className="form-group">
              <input
                type="number"
                className="form-input"
                placeholder="Paid Amount"
                value={formData.paid_amount}
                onChange={(e) => setFormData({...formData, paid_amount: e.target.value})}
                step="0.01"
              />
            </div>
          )}

          <div className="summary-container">
            <div className="summary-row">
              <span className="summary-label">Total Amount:</span>
              <span className="summary-value">{calculateTotal()} ብር</span>
            </div>
            
            {formData.payment_method === 'mixed' && (
              <div className="summary-row">
                <span className="summary-label">Unpaid Amount:</span>
                <span className="summary-value">{calculateUnpaid()} ብር</span>
              </div>
            )}
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Place Order'}
          </button>
        </form>
      </div>
    </div>
  );

  return showProductList ? renderProductList() : renderOrderForm();
};

export default ProductOrder;
